import {
  Box,
  InputAdornment,
  List,
  ListItemButton,
  ListSubheader,
  Popover,
  PopoverPosition,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { KeyboardEvent, useEffect, useMemo, useState } from "react";
import { EmailVariable } from "./types";

interface VariableMenuProps {
  variables: EmailVariable[];
  anchorEl?: HTMLElement | null;
  anchorPosition?: PopoverPosition | null;
  onClose: () => void;
  onSelect: (variable: EmailVariable) => void;
}

const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export default function VariableMenu({
  variables,
  anchorEl,
  anchorPosition,
  onClose,
  onSelect,
}: VariableMenuProps) {
  const theme = useTheme();
  const open = !!anchorEl || !!anchorPosition;
  const [search, setSearch] = useState("");
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    if (open) {
      setSearch("");
      setHighlight(0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const term = normalize(search.trim());
    if (!term) return variables;
    return variables.filter((v) =>
      normalize(`${v.label} ${v.grupo} ${v.token}`).includes(term),
    );
  }, [variables, search]);

  const groups = useMemo(() => {
    const map = new Map<string, EmailVariable[]>();
    filtered.forEach((v) => map.set(v.grupo, [...(map.get(v.grupo) ?? []), v]));
    return Array.from(map.entries());
  }, [filtered]);

  const ordered = groups.flatMap(([, items]) => items);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, ordered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && ordered[highlight]) {
      e.preventDefault();
      onSelect(ordered[highlight]);
    }
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
      anchorPosition={anchorPosition ?? undefined}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            width: 320,
            maxHeight: 420,
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${theme.palette.stroke}`,
          },
        },
      }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Buscar informação"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={handleKeyDown}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <List dense sx={{ overflowY: "auto", pt: 0 }}>
        {groups.map(([grupo, items]) => (
          <Box key={grupo}>
            <ListSubheader
              sx={{
                lineHeight: "32px",
                fontSize: 12,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              {grupo}
            </ListSubheader>
            {items.map((v) => (
              <ListItemButton
                key={v.token}
                selected={ordered[highlight]?.token === v.token}
                onClick={() => onSelect(v)}
                sx={{ flexDirection: "column", alignItems: "flex-start", py: 0.75 }}
              >
                <Typography fontSize={14}>{v.label}</Typography>
                <Typography fontSize={12} color="text.secondary" noWrap sx={{ maxWidth: "100%" }}>
                  {v.exemplo}
                </Typography>
              </ListItemButton>
            ))}
          </Box>
        ))}
        {groups.length === 0 && (
          <Typography fontSize={14} color="text.secondary" sx={{ px: 2, py: 1.5 }}>
            Nada encontrado
          </Typography>
        )}
      </List>
    </Popover>
  );
}
